import React, { useMemo, useRef, useState } from "react";
import { Animated, Pressable, ScrollView, Text, View, useWindowDimensions } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useToast } from "../../components/LhToastContainer";
import styles from "./CategoryMapping.styles";
import {
  AddCategoryWizard,
  AddSubcategoryModal,
  AddTertiaryModal,
  AdditionalFieldsModal,
  EditCategoryModal,
  EditSubcategoryModal,
  EditTertiaryModal,
  useTicketingConfigModals,
} from "./components";
import type { AdditionalField, CategoryCreationDraft, WizardTeamOption } from "./components";
import ticketingConfigApi from "../../api/ticketingConfigApi";
import {
  CategoryMappingFieldResponse,
  TicketingNodeType,
} from "../../types/ticketingConfig.types";
import { useCategoryMappingFields } from "./hooks/useCategoryMappingFields";
import { getApiErrorMessage } from "./hooks/useTicketingConfig";
import type {
  CategoryReviewSaveRequest,
  ReviewFieldConfig,
  ReviewFieldType,
  TeamAssignmentRequest,
} from "../../types/ticketingConfig.types";

/**
 * Category Mapping tab — converted from ticketing-portal-web-configs_2.html
 * lines 6398-6464 (#configTab-category): a three-panel taxonomy builder for
 * Categories -> Subcategories -> Tertiary values used on the Submit Ticket form.
 */

// ── Types ───────────────────────────────────────────────────────────────────

export interface FieldConfig {
  id: string;
  label: string;
  type: "text" | "dropdown";
  required: boolean;
  options: string[];
}

export interface CategoryItem {
  id: string;
  name: string;
  active?: boolean;
  subcategoryCount?: number;
  fields?: FieldConfig[];
}

export interface SubcategoryItem {
  id: string;
  categoryId: string;
  name: string;
  active?: boolean;
  tertiaryCategoryCount?: number;
  fields?: FieldConfig[];
}

export interface TertiaryItem {
  id: string;
  subcategoryId: string;
  name: string;
  active?: boolean;
  fields?: FieldConfig[];
}

const slugifyFieldName = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const generateFieldNameSuffix = () => Math.floor(Math.random() * 0x100000000).toString(16).padStart(8, "0");

const getUniqueFieldNames = (
  fields: AdditionalField[],
  nodeType?: TicketingNodeType,
  nodeId?: number
) => {
  const usedNames = new Set<string>();

  return fields.map((field) => {
    const existingFieldName = field.fieldName.trim();
    const titleSlug = slugifyFieldName(field.fieldLabel);
    const generatedBaseName =
      nodeType && Number.isFinite(nodeId)
        ? `cm_${nodeType.toLowerCase()}_${nodeId}_${titleSlug}`
        : titleSlug;
    const baseName = existingFieldName || generatedBaseName;
    if (!baseName) return "";

    let fieldName = baseName;
    let suffix = 2;
    if (!existingFieldName && nodeType && Number.isFinite(nodeId)) {
      fieldName = `${baseName}_${generateFieldNameSuffix()}`;
      while (usedNames.has(fieldName.toLowerCase())) {
        fieldName = `${baseName}_${generateFieldNameSuffix()}`;
      }
    } else {
      while (usedNames.has(fieldName.toLowerCase())) {
        fieldName = `${baseName}_${suffix}`;
        suffix += 1;
      }
    }

    usedNames.add(fieldName.toLowerCase());
    return fieldName;
  });
};

const parseOptionalNumber = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeFieldType = (type: string): AdditionalField["type"] => {
  const normalized = type.toLowerCase();
  switch (normalized) {
    case "textarea":
    case "text_multi":
    case "text_url":
    case "email_multi":
    case "single_select":
    case "single_select_searchable":
    case "multi_select":
    case "multi_select_searchable":
    case "auto_populated":
    case "file_upload":
      return normalized as AdditionalField["type"];
    case "text":
    default:
      return "text";
  }
};

const toReviewFieldType = (type: AdditionalField["type"]): ReviewFieldType => {
  if (type === "text") return "TEXT";
  return type.toUpperCase() as ReviewFieldType;
};

const mapFieldsForReview = (fields: AdditionalField[] | undefined): ReviewFieldConfig[] | undefined => {
  const configuredFields = (fields ?? []).filter((field) => field.fieldLabel.trim());
  if (!configuredFields.length) return undefined;
  const fieldNames = getUniqueFieldNames(configuredFields);
  return configuredFields.map((field, index) => ({
    fieldName: fieldNames[index] || undefined,
    fieldLabel: field.fieldLabel.trim(),
    fieldType: toReviewFieldType(field.type),
    isRequired: field.required,
    displayOrder: index + 1,
    charLimit: parseOptionalNumber(field.charLimit) ?? null,
    dropdownSource: field.dropdownSource || null,
    placeholder: field.placeholder.trim() || null,
    tooltip: field.tooltip.trim() || null,
    options: field.options.length
      ? field.options.map((option, optionIndex) => ({
          optionLabel: option.trim(),
          optionValue: slugifyFieldName(option),
          displayOrder: optionIndex,
        })).filter((option) => option.optionLabel)
      : undefined,
  }));
};

const mapAssignmentForReview = (
  assignment: CategoryCreationDraft["assignments"][string] | undefined,
  teams: WizardTeamOption[]
): TeamAssignmentRequest | undefined => {
  if (!assignment?.organization && !assignment?.team) return undefined;
  const team = teams.find((option) => String(option.id) === assignment.team);
  return {
    organizationName: assignment.organization || null,
    teamId: team?.id ?? (assignment.team ? Number(assignment.team) : null),
    active: true,
  };
};

const mapFieldResponseToDraft = (field: CategoryMappingFieldResponse): AdditionalField => ({
  id: `db-${field.id}`,
  backendId: field.id,
  fieldName: field.fieldName ?? "",
  fieldLabel: field.fieldLabel ?? "",
  type: normalizeFieldType(field.fieldType),
  required: field.isRequired ?? field.required ?? false,
  charLimit: field.charLimit != null ? String(field.charLimit) : "",
  dropdownSource: field.dropdownSource ?? "",
  placeholder: field.placeholder ?? "",
  tooltip: field.tooltip ?? "",
  options: [...(field.options ?? [])]
    .sort((left, right) => left.displayOrder - right.displayOrder)
    .map((option) => option.optionLabel),
});

export interface CategoryMappingProps {
  categories?: CategoryItem[];
  subcategories?: SubcategoryItem[];
  tertiaries?: TertiaryItem[];
  /** True while categories/subcategories/tertiaries are being (re)loaded. */
  loading?: boolean;
  /** True while subcategories for the selected category are being (re)loaded. */
  subcategoriesLoading?: boolean;
  /** Fired when a category row is selected — should load its subcategories (GET /categories/{id}/subcategories). */
  onSelectCategory?: (categoryId: string) => void;
  /** Fired when a subcategory row is selected — should load its tertiary values (GET /subcategories/{id}/tertiary). */
  onSelectSubcategory?: (subcategoryId: string) => void;
  /** Fired when the Add Category modal is saved. Should persist via the API and resolve once the taxonomy has refreshed. */
  onAddCategory?: (name: string) => Promise<void> | void;
  /** Fired when the Add Subcategory modal is saved for the given category. */
  onAddSubcategory?: (categoryId: string, name: string, fields?: ReviewFieldConfig[]) => Promise<void> | void;
  /** Fired when the Add Tertiary modal is saved for the given subcategory. */
  onAddTertiary?: (categoryId: string, subcategoryId: string, name: string, fields?: ReviewFieldConfig[]) => Promise<void> | void;
  /** Fired when the Edit Category modal is saved. */
  onEditCategory?: (categoryId: string, name: string) => Promise<void> | void;
  /** Fired when the Edit Subcategory modal is saved. */
  onEditSubcategory?: (subcategoryId: string, name: string) => Promise<void> | void;
  /** Fired when the Edit Tertiary modal is saved. */
  onEditTertiary?: (tertiaryId: string, name: string) => Promise<void> | void;
  /** Fired when a category's active/inactive toggle is switched. */
  onToggleCategory?: (categoryId: string, active: boolean) => Promise<void> | void;
  /** Fired when a subcategory's active/inactive toggle is switched. */
  onToggleSubcategory?: (subcategoryId: string, active: boolean) => Promise<void> | void;
  /** Fired when a tertiary's active/inactive toggle is switched. */
  onToggleTertiary?: (tertiaryId: string, active: boolean) => Promise<void> | void;
  /**
   * When provided (Sustain Team Member with role "Member"), only the category IDs
   * in this set can be edited/toggled/added to. Leads receive `undefined` (no restriction).
   */
  editableCategoryIds?: Set<string>;
  /**
   * When provided, only the subcategory IDs in this set can be edited/toggled/added to.
   */
  editableSubcategoryIds?: Set<string>;
  /**
   * When provided, only the tertiary IDs in this set can be edited/toggled/added to.
   */
  editableTertiaryIds?: Set<string>;
  teams?: WizardTeamOption[];
  onCategoryCreated?: () => Promise<void> | void;
}

// ── Toggle switch — same pattern as StatusToggle in TeamAssignment ────────────

interface ToggleSwitchProps {
  value: boolean;
  onValueChange: (v: boolean) => void;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ value, onValueChange }) => {
  const translateX = useRef(new Animated.Value(value ? 16 : 0)).current;

  React.useEffect(() => {
    Animated.timing(translateX, {
      toValue: value ? 16 : 0,
      duration: 150,
      useNativeDriver: true,
    }).start();
  }, [value]);

  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityState={{ checked: value }}
      onPress={(e) => {
        e.stopPropagation?.();
        onValueChange(!value);
      }}
      style={[styles.toggleTrack, value ? styles.toggleTrackOn : styles.toggleTrackOff]}
    >
      <Animated.View style={[styles.toggleThumb, { transform: [{ translateX }] }]} />
    </Pressable>
  );
};

// ── Generic list-panel row ────────────────────────────────────────────────────

interface RowProps {
  label: string;
  subLabel?: string;
  count?: number;
  fieldsCount?: number;
  /** Whether this row is the currently selected/highlighted row */
  selected: boolean;
  /** Whether the item is active (toggle state) */
  itemActive?: boolean;
  onPress?: () => void;
  onEdit?: () => void;
  onToggle?: (value: boolean) => void;
  onAddFields?: () => void;
}

const ListRow: React.FC<RowProps> = ({
  label,
  subLabel,
  count,
  fieldsCount = 0,
  selected,
  itemActive = true,
  onPress,
  onEdit,
  onToggle,
  onAddFields,
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      // @ts-ignore — web-only hover handlers, no-op on native
      onMouseEnter={() => setHovered(true)}
      // @ts-ignore
      onMouseLeave={() => setHovered(false)}
      style={[styles.listItem, selected && styles.listItemActive, !selected && hovered && styles.listItemHovered, !itemActive && styles.listItemInactive]}
    >
      <View style={styles.listItemTextWrap}>
        <Text style={[styles.listItemLabel, selected && styles.listItemLabelActive]}>
          {label}
        </Text>
        {subLabel ? <Text style={styles.listItemSubLabel} numberOfLines={1}>{subLabel}</Text> : null}
      </View>
      {typeof count === "number" ? (
        <View style={styles.listItemCount}>
          <Text style={styles.listItemCountText}>{count}</Text>
        </View>
      ) : null}
      {onAddFields ? (
        <Pressable
          style={styles.addFieldsRowButton}
          onPress={(e) => {
            e.stopPropagation?.();
            onAddFields();
          }}
        >
          <MaterialIcons name="add" size={14} color="#034895" />
          <Text style={styles.addFieldsRowButtonText}>Add Fields ({fieldsCount})</Text>
        </Pressable>
      ) : null}
      <View style={styles.listItemActions}>
        <Pressable
          style={styles.listItemActionBtn}
          onPress={(e) => {
            e.stopPropagation?.();
            onEdit?.();
          }}
        >
          <MaterialIcons name="edit" size={14} color="#818181" />
        </Pressable>
        {onToggle !== undefined && (
          <ToggleSwitch value={itemActive} onValueChange={onToggle} />
        )}
      </View>
    </Pressable>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const CategoryMapping: React.FC<CategoryMappingProps> = ({
  categories = [],
  subcategories = [],
  tertiaries = [],
  loading = false,
  onAddCategory,
  onAddSubcategory,
  onAddTertiary,
  onEditCategory,
  onEditSubcategory,
  onEditTertiary,
  onToggleCategory,
  onToggleSubcategory,
  onToggleTertiary,
  subcategoriesLoading = false,
  onSelectCategory,
  onSelectSubcategory,
  editableCategoryIds,
  editableSubcategoryIds,
  editableTertiaryIds,
  teams = [],
  onCategoryCreated,
}) => {
  const { width } = useWindowDimensions();
  const isStacked = width < 900;
  const toast = useToast();

  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [activeSubcategoryId, setActiveSubcategoryId] = useState<string | null>(null);
  const [activeTertiaryId, setActiveTertiaryId] = useState<string | null>(null);

  // Existing fields (GET /ticketing/config/fields) for whichever node is currently selected in each panel.
  const categoryFieldsQuery = useCategoryMappingFields({
    categoryId: activeCategoryId ? Number(activeCategoryId) : undefined,
  });
  const subcategoryFieldsQuery = useCategoryMappingFields({
    categoryId: activeCategoryId ? Number(activeCategoryId) : undefined,
    subcategoryId: activeSubcategoryId ? Number(activeSubcategoryId) : undefined,
  });
  const tertiaryFieldsQuery = useCategoryMappingFields({
    categoryId: activeCategoryId ? Number(activeCategoryId) : undefined,
    subcategoryId: activeSubcategoryId ? Number(activeSubcategoryId) : undefined,
    tertiaryId: activeTertiaryId ? Number(activeTertiaryId) : undefined,
  });

  // Modal state management
  const {
    modals,
    selectedContext,
    openAddCategoryModal,
    closeAddCategoryModal,
    openAddSubcategoryModal,
    closeAddSubcategoryModal,
    openAddTertiaryModal,
    closeAddTertiaryModal,
    openAdditionalFieldsModal,
    closeAdditionalFieldsModal,
    openEditCategoryModal,
    closeEditCategoryModal,
    openEditSubcategoryModal,
    closeEditSubcategoryModal,
    openEditTertiaryModal,
    closeEditTertiaryModal,
    openCategoryFieldsModal,
    openSubcategoryFieldsModal,
    openTertiaryFieldsModal,
  } = useTicketingConfigModals();

  // Local state management for categories/subcategories/tertiary
  const [localCategories, setLocalCategories] = useState<CategoryItem[]>(
    categories.map((c) => ({ active: true, ...c }))
  );
  const [localSubcategories, setLocalSubcategories] = useState<SubcategoryItem[]>(
    subcategories.map((s) => ({ active: true, ...s }))
  );
  const [localTertiaries, setLocalTertiaries] = useState<TertiaryItem[]>(
    tertiaries.map((t) => ({ active: true, ...t }))
  );

  // Data arrives asynchronously after mount — keep local lists in sync with props.
  React.useEffect(() => {
    setLocalCategories(categories.map((c) => ({ active: true, ...c })));
  }, [categories]);
  React.useEffect(() => {
    setLocalSubcategories(subcategories.map((s) => ({ active: true, ...s })));
  }, [subcategories]);
  React.useEffect(() => {
    setLocalTertiaries(tertiaries.map((t) => ({ active: true, ...t })));
  }, [tertiaries]);

  const activeCategory = useMemo(
    () => localCategories.find((c) => c.id === activeCategoryId) ?? null,
    [localCategories, activeCategoryId]
  );

  const visibleSubcategories = useMemo(
    () => (activeCategoryId ? localSubcategories.filter((s) => s.categoryId === activeCategoryId) : []),
    [localSubcategories, activeCategoryId]
  );

  const activeSubcategory = useMemo(
    () => visibleSubcategories.find((s) => s.id === activeSubcategoryId) ?? null,
    [visibleSubcategories, activeSubcategoryId]
  );

  const visibleTertiaries = useMemo(
    () => (activeSubcategoryId ? localTertiaries.filter((t) => t.subcategoryId === activeSubcategoryId) : []),
    [localTertiaries, activeSubcategoryId]
  );

  function handleSelectCategory(category: CategoryItem) {
    setActiveCategoryId(category.id);
    setActiveSubcategoryId(null);
    onSelectCategory?.(category.id);
  }

  function handleSelectSubcategory(subcategory: SubcategoryItem) {
    setActiveSubcategoryId(subcategory.id);
    setActiveTertiaryId(null);
    onSelectSubcategory?.(subcategory.id);
  }

  function handleSelectTertiary(tertiary: TertiaryItem) {
    setActiveTertiaryId((prev) => (prev === tertiary.id ? null : tertiary.id));
  }

  // Modal callback handlers — persist via the API; local lists refresh from props once the parent refetches.
  const handleAddCategory = async (categoryName: string) => {
    try {
      await onAddCategory?.(categoryName);
      closeAddCategoryModal();
      toast.show(`Category "${categoryName}" added successfully`, "success");
    } catch (e: any) {
      toast.show(e?.message || "Failed to add category", "error");
    }
  };

  const handleSaveCategoryWizard = async (draft: CategoryCreationDraft) => {
    try {
      const categoryKey = `${draft.categoryName}:category`;
      const request: CategoryReviewSaveRequest = {
        categoryName: draft.categoryName,
        teamAssignment: mapAssignmentForReview(draft.assignments[categoryKey], teams),
        fields: mapFieldsForReview(draft.fields.category),
        subCategories: draft.noSubcategories ? undefined : draft.subcategories.map((subcategory) => {
          const subcategoryIndex = draft.subcategories.indexOf(subcategory);
          const subcategoryKey = `${subcategory.name}:category`;
          const tertiaries = subcategory.noTertiary ? [] : subcategory.tertiaries
            .map((tertiary, tertiaryIndex) => {
              const tertiaryKey = `tertiary:${subcategoryIndex}:${tertiaryIndex}`;
              return {
                name: tertiary.name.trim(),
                teamAssignment: mapAssignmentForReview(
                  draft.assignments[`${subcategory.name}:${tertiary.name}`],
                  teams
                ),
                fields: mapFieldsForReview(draft.fields[tertiaryKey]),
              };
            })
            .filter((tertiary) => tertiary.name);
          return {
            name: subcategory.name.trim(),
            teamAssignment: mapAssignmentForReview(draft.assignments[subcategoryKey], teams),
            fields: mapFieldsForReview(draft.fields[`subcategory:${subcategoryIndex}`]),
            ...(tertiaries.length ? { tertiaries } : { tertiaryNames: [] }),
          };
        }),
      };
      await ticketingConfigApi.reviewSave(request);
      closeAddCategoryModal();
      toast.show("Save Successful", "success");
      await onCategoryCreated?.();
    } catch (e: any) {
      toast.show(getApiErrorMessage(e), "error", "Ticketing Configurations");
      throw e;
    }
  };

  const handleAddSubcategory = async (subcategoryName: string, fields?: AdditionalField[]) => {
    if (!selectedContext.category) return;
    try {
      await onAddSubcategory?.(selectedContext.category, subcategoryName, mapFieldsForReview(fields));
      closeAddSubcategoryModal();
      toast.show(`Subcategory "${subcategoryName}" added successfully`, "success");
    } catch (e: any) {
      toast.show(e?.message || "Failed to add subcategory", "error");
    }
  };

  const handleAddTertiary = async (tertiaryName: string, fields?: AdditionalField[]) => {
    if (!selectedContext.subcategory) return;
    try {
      await onAddTertiary?.(selectedContext.category, selectedContext.subcategory, tertiaryName, mapFieldsForReview(fields));
      closeAddTertiaryModal();
      toast.show(`Tertiary Category "${tertiaryName}" added successfully`, "success");
    } catch (e: any) {
      toast.show(e?.message || "Failed to add tertiary category", "error");
    }
  };

  const handleEditCategory = async (categoryName: string) => {
    if (!selectedContext.category) return;
    try {
      await onEditCategory?.(selectedContext.category, categoryName);
      closeEditCategoryModal();
      toast.show(`Category updated to "${categoryName}"`, "success");
    } catch (e: any) {
      toast.show(e?.message || "Failed to update category", "error");
    }
  };

  const handleEditSubcategory = async (subcategoryName: string) => {
    if (!selectedContext.subcategory) return;
    try {
      await onEditSubcategory?.(selectedContext.subcategory, subcategoryName);
      closeEditSubcategoryModal();
      toast.show(`Subcategory updated to "${subcategoryName}"`, "success");
    } catch (e: any) {
      toast.show(e?.message || "Failed to update subcategory", "error");
    }
  };

  const handleEditTertiary = async (tertiaryName: string) => {
    if (!selectedContext.tertiary) return;
    try {
      await onEditTertiary?.(selectedContext.tertiary, tertiaryName);
      closeEditTertiaryModal();
      toast.show(`Tertiary Category updated to "${tertiaryName}"`, "success");
    } catch (e: any) {
      toast.show(e?.message || "Failed to update tertiary category", "error");
    }
  };

  const handleToggleCategory = async (id: string, value: boolean) => {
    try {
      await onToggleCategory?.(id, value);
    } catch (e: any) {
      toast.show(e?.message || "Failed to update category status", "error");
    }
  };

  const handleToggleSubcategory = async (id: string, value: boolean) => {
    try {
      await onToggleSubcategory?.(id, value);
    } catch (e: any) {
      toast.show(e?.message || "Failed to update subcategory status", "error");
    }
  };

  const handleToggleTertiary = async (id: string, value: boolean) => {
    try {
      await onToggleTertiary?.(id, value);
    } catch (e: any) {
      toast.show(e?.message || "Failed to update tertiary status", "error");
    }
  };

  const modalNodeType: TicketingNodeType | undefined =
    selectedContext.modalType === "category"
      ? "CATEGORY"
      : selectedContext.modalType === "subcategory"
      ? "SUBCATEGORY"
      : selectedContext.modalType === "tertiary"
      ? "TERTIARY"
      : undefined;

  const modalNodeId =
    selectedContext.modalType === "tertiary"
      ? selectedContext.tertiary
      : selectedContext.modalType === "subcategory"
      ? selectedContext.subcategory
      : selectedContext.category;

  // Reuse whichever panel query already fetched this node's fields instead of firing a duplicate
  // request — openFieldsEditorFor*/the edit-modal handlers below keep active*Id in sync with the
  // node the Additional Fields modal targets, so the matching query is always already loaded.
  const modalFieldsQuery =
    modalNodeType === "SUBCATEGORY"
      ? subcategoryFieldsQuery
      : modalNodeType === "TERTIARY"
      ? tertiaryFieldsQuery
      : categoryFieldsQuery;

  const [fieldsSaveError, setFieldsSaveError] = useState<string | null>(null);

  const openFieldsEditorForCategory = (categoryId: string) => {
    setFieldsSaveError(null);
    setActiveCategoryId(categoryId);
    setActiveSubcategoryId(null);
    setActiveTertiaryId(null);
    openCategoryFieldsModal(categoryId);
  };

  const openFieldsEditorForSubcategory = (categoryId: string, subcategoryId: string) => {
    setFieldsSaveError(null);
    setActiveCategoryId(categoryId);
    setActiveSubcategoryId(subcategoryId);
    setActiveTertiaryId(null);
    openSubcategoryFieldsModal(categoryId, subcategoryId);
  };

  const openFieldsEditorForTertiary = (categoryId: string, subcategoryId: string, tertiaryId: string) => {
    setFieldsSaveError(null);
    setActiveCategoryId(categoryId);
    setActiveSubcategoryId(subcategoryId);
    setActiveTertiaryId(tertiaryId);
    openTertiaryFieldsModal(categoryId, subcategoryId, tertiaryId);
  };

  // Keep active*Id in sync so that if the user opens the fields editor from inside the edit
  // modal (onOpenFieldsModal), the reused panel query already points at the right node.
  const openEditSubcategoryEditor = (categoryId: string, subcategoryId: string) => {
    setActiveCategoryId(categoryId);
    setActiveSubcategoryId(subcategoryId);
    openEditSubcategoryModal(categoryId, subcategoryId);
  };

  const openEditTertiaryEditor = (categoryId: string, subcategoryId: string, tertiaryId: string) => {
    setActiveCategoryId(categoryId);
    setActiveSubcategoryId(subcategoryId);
    setActiveTertiaryId(tertiaryId);
    openEditTertiaryModal(categoryId, subcategoryId, tertiaryId);
  };

  const handleSaveFields = async (fields: AdditionalField[]) => {
    const nodeId = modalNodeId ? Number(modalNodeId) : NaN;
    if (!modalNodeType || !Number.isFinite(nodeId)) {
      toast.show("Select a category, subcategory, or tertiary value first", "error");
      closeAdditionalFieldsModal();
      return;
    }

    setFieldsSaveError(null);

    // Ancestor ids the backend requires alongside nodeId for SUBCATEGORY/TERTIARY nodes.
    const categoryId = selectedContext.category ? Number(selectedContext.category) : undefined;
    const subCategoryId = selectedContext.subcategory ? Number(selectedContext.subcategory) : undefined;

    try {
      const fieldNames = getUniqueFieldNames(fields, modalNodeType, nodeId);
      for (let index = 0; index < fields.length; index += 1) {
        const field = fields[index];
        const fieldName = fieldNames[index];
        const fieldLabel = field.fieldLabel.trim();
        const fieldType = field.type;
        const freeTextType =
          fieldType === "text" ||
          fieldType === "textarea" ||
          fieldType === "text_multi" ||
          fieldType === "text_url" ||
          fieldType === "email_multi";
        const shouldSendOptions =
          (fieldType === "single_select" ||
            fieldType === "single_select_searchable" ||
            fieldType === "multi_select" ||
            fieldType === "multi_select_searchable") &&
          field.dropdownSource === "static_options";
        const shouldSendDropdownSource =
          fieldType === "single_select" ||
          fieldType === "single_select_searchable" ||
          fieldType === "multi_select" ||
          fieldType === "multi_select_searchable";
        const payload = {
          fieldName: fieldName || undefined,
          fieldLabel,
          fieldType,
          required: field.required,
          displayOrder: index + 1,
          charLimit: freeTextType ? parseOptionalNumber(field.charLimit) : undefined,
          dropdownSource: shouldSendDropdownSource && field.dropdownSource ? field.dropdownSource : undefined,
          placeholder: freeTextType ? field.placeholder.trim() || undefined : undefined,
          tooltip: field.tooltip.trim() || undefined,
          options: shouldSendOptions
            ? field.options
                .map((option) => option.trim())
                .filter(Boolean)
                .map((option, optionIndex) => ({
                  optionLabel: option,
                  optionValue: option,
                  displayOrder: optionIndex,
                }))
            : undefined,
        };

        if (field.backendId) {
          await ticketingConfigApi.updateField(field.backendId, payload);
          continue;
        }

        await ticketingConfigApi.addField({
          nodeType: modalNodeType,
          nodeId,
          categoryId: modalNodeType !== "CATEGORY" ? categoryId : undefined,
          subCategoryId: modalNodeType === "TERTIARY" ? subCategoryId : undefined,
          ...payload,
        });
      }

      // Refetch all three panel queries — modalFieldsQuery is one of them, so this covers the modal too.
      await Promise.all([
        categoryFieldsQuery.refetch(),
        subcategoryFieldsQuery.refetch(),
        tertiaryFieldsQuery.refetch(),
      ]);

      closeAdditionalFieldsModal();
      toast.show(`${fields.length} field(s) saved successfully`, "success");
    } catch (e: any) {
      const message = getApiErrorMessage(e);
      setFieldsSaveError(message);
      const statusCode = Number(e?.response?.status ?? e?.response?.data?.statusCode ?? e?.statusCode);
      if (statusCode !== 409) {
        toast.show(message, "error", "Ticketing Configurations");
      }
    }
  };

  const handleDeleteField = async (field: AdditionalField) => {
    if (!field.backendId) {
      return;
    }

    try {
      await ticketingConfigApi.setFieldStatus(field.backendId, false);
      await Promise.all([
        categoryFieldsQuery.refetch(),
        subcategoryFieldsQuery.refetch(),
        tertiaryFieldsQuery.refetch(),
      ]);
    } catch (e: any) {
      throw new Error(getApiErrorMessage(e));
    }
  };

  // Pre-populate the Additional Fields modal with whatever is already persisted for the selected
  // node. Guard against a not-yet-created subcategory/tertiary (empty modalNodeId) leaking whatever
  // fields the reused panel query happens to hold for a different, currently-active node.
  const modalInitialFields: AdditionalField[] = useMemo(
    () => (modalNodeId ? modalFieldsQuery.fields.map(mapFieldResponseToDraft) : []),
    [modalNodeId, modalFieldsQuery.fields]
  );
  const modalFieldsLoading = modalNodeId ? modalFieldsQuery.loading : false;

  return (
    <View style={[styles.layout, isStacked && styles.layoutStacked]}>
      {/* ── Panel 1: Categories ── */}
      <View style={[isStacked ? styles.panelFlexStacked : styles.panelFlexColumn]}>
        <View style={styles.panel}>
          <View style={styles.panelHeader}>
            <View style={styles.panelHeading}>
              <View style={styles.panelTitleRow}>
                <Text style={styles.panelTitle}>Categories</Text>
                <View style={styles.panelCount}>
                  <Text style={styles.panelCountText}>{categories.length}</Text>
                </View>
              </View>
            </View>
            {/* Members cannot add new categories — only Leads can */}
            {editableCategoryIds === undefined && (
              <Pressable style={styles.btnSm} onPress={openAddCategoryModal}>
                <MaterialIcons name="add" size={13} color="#034895" />
                <Text style={styles.btnSmText}>Add Categories</Text>
              </Pressable>
            )}
          </View>

          <ScrollView style={styles.panelBodyScroll} contentContainerStyle={styles.panelBody}>
            {loading && localCategories.length === 0 ? (
              <View style={styles.emptyPanel}>
                <Text style={styles.emptyPanelText}>Loading categories…</Text>
              </View>
            ) : localCategories.length === 0 ? (
              <View style={styles.emptyPanel}>
                <MaterialIcons name="category" size={24} color="#c0c8d4" />
                <Text style={styles.emptyPanelText}>No categories configured yet.</Text>
              </View>
            ) : (
              localCategories.map((category) => {
                const isCatEditable =
                  editableCategoryIds === undefined || editableCategoryIds.has(category.id);
                return (
                  <React.Fragment key={category.id}>
                    <ListRow
                      label={category.name}
                      count={category.subcategoryCount ?? localSubcategories.filter((s) => s.categoryId === category.id).length}
                      fieldsCount={
                        category.id === activeCategoryId
                          ? categoryFieldsQuery.fields.length
                          : category.fields?.length ?? 0
                      }
                      selected={category.id === activeCategoryId}
                      itemActive={category.active !== false}
                      onPress={() => handleSelectCategory(category)}
                      onAddFields={isCatEditable ? () => openFieldsEditorForCategory(category.id) : undefined}
                      onEdit={isCatEditable ? () => openEditCategoryModal(category.id) : undefined}
                      onToggle={isCatEditable ? (val) => handleToggleCategory(category.id, val) : undefined}
                    />
                  </React.Fragment>
                );
              })
            )}
          </ScrollView>
        </View>
      </View>

      {/* ── Panel 2: Subcategories ── */}
      <View style={[isStacked ? styles.panelFlexStacked : styles.panelFlexColumn]}>
        <View style={styles.panel}>
          <View style={styles.panelHeader}>
            <View style={styles.panelHeading}>
              <View style={styles.panelTitleRow}>
                <Text style={styles.panelTitle}>Subcategories</Text>
                <View style={styles.panelCount}>
                  <Text style={styles.panelCountText}>{visibleSubcategories.length}</Text>
                </View>
              </View>
              <Text style={styles.panelContext}>
                {activeCategory ? activeCategory.name : "Select a category"}
              </Text>
            </View>
            <Pressable
              style={[styles.btnSm, (!activeCategoryId || (editableSubcategoryIds !== undefined && !editableCategoryIds?.has(activeCategoryId))) && styles.btnSmDisabled]}
              disabled={!activeCategoryId || (editableSubcategoryIds !== undefined && !editableCategoryIds?.has(activeCategoryId))}
              onPress={() => activeCategoryId && openAddSubcategoryModal(activeCategoryId)}
            >
              <MaterialIcons name="add" size={13} color="#034895" />
              <Text style={styles.btnSmText}>Add Subcategory</Text>
            </Pressable>
          </View>

          <ScrollView style={styles.panelBodyScroll} contentContainerStyle={styles.panelBody}>
            {!activeCategoryId ? (
              <View style={styles.emptyPanel}>
                <MaterialIcons name="arrow-back" size={24} color="#c0c8d4" />
                <Text style={styles.emptyPanelText}>No Category Selected, Please Select a Category to View Subcategory Options.</Text>
              </View>
            ) : subcategoriesLoading && visibleSubcategories.length === 0 ? (
              <View style={styles.emptyPanel}>
                <Text style={styles.emptyPanelText}>Loading subcategories…</Text>
              </View>
            ) : visibleSubcategories.length === 0 ? (
              <View style={styles.emptyPanel}>
                <MaterialIcons name="subdirectory-arrow-right" size={24} color="#c0c8d4" />
                <Text style={styles.emptyPanelText}>No subcategories configured for this category yet.</Text>
              </View>
            ) : (
              visibleSubcategories.map((subcategory) => {
                const isSubEditable =
                  editableSubcategoryIds === undefined || editableSubcategoryIds.has(subcategory.id);
                return (
                  <React.Fragment key={subcategory.id}>
                    <ListRow
                      label={subcategory.name}
                      count={
                        subcategory.tertiaryCategoryCount ??
                        localTertiaries.filter((t) => t.subcategoryId === subcategory.id).length
                      }
                      fieldsCount={
                        subcategory.id === activeSubcategoryId
                          ? subcategoryFieldsQuery.fields.length
                          : subcategory.fields?.length ?? 0
                      }
                      selected={subcategory.id === activeSubcategoryId}
                      itemActive={subcategory.active !== false}
                      onPress={() => handleSelectSubcategory(subcategory)}
                      onAddFields={isSubEditable ? () =>
                        openFieldsEditorForSubcategory(activeCategoryId || "", subcategory.id)
                        : undefined}
                      onEdit={isSubEditable ? () => openEditSubcategoryEditor(activeCategoryId || "", subcategory.id) : undefined}
                      onToggle={isSubEditable ? (val) => handleToggleSubcategory(subcategory.id, val) : undefined}
                    />
                  </React.Fragment>
                );
              })
            )}
          </ScrollView>
        </View>
      </View>

      {/* ── Panel 3: Tertiary ── */}
      <View style={[isStacked ? styles.panelFlexStacked : styles.panelFlexColumn]}>
        <View style={styles.panel}>
          <View style={styles.panelHeader}>
            <View style={styles.panelHeading}>
              <View style={styles.panelTitleRow}>
                <Text style={styles.panelTitle}>Tertiary</Text>
                <View style={styles.panelCount}>
                  <Text style={styles.panelCountText}>{visibleTertiaries.length}</Text>
                </View>
              </View>
              <Text style={styles.panelContext}>
                {activeSubcategory ? activeSubcategory.name : "Select a subcategory"}
              </Text>
            </View>
            <Pressable
              style={[styles.btnSm, (!activeSubcategoryId || (editableTertiaryIds !== undefined && !editableSubcategoryIds?.has(activeSubcategoryId))) && styles.btnSmDisabled]}
              disabled={!activeSubcategoryId || (editableTertiaryIds !== undefined && !editableSubcategoryIds?.has(activeSubcategoryId))}
              onPress={() =>
                activeCategoryId && activeSubcategoryId && openAddTertiaryModal(activeCategoryId, activeSubcategoryId)
              }
            >
              <MaterialIcons name="add" size={13} color="#034895" />
              <Text style={styles.btnSmText}>Add Tertiary</Text>
            </Pressable>
          </View>

          <ScrollView style={styles.panelBodyScroll} contentContainerStyle={styles.panelBody}>
            {!activeSubcategoryId ? (
              <View style={styles.emptyPanel}>
                <MaterialIcons name="arrow-back" size={24} color="#c0c8d4" />
                <Text style={styles.emptyPanelText}>No SubCategory Selected, Please Select a SubCategory to View Tertiary Options.</Text>
              </View>
            ) : visibleTertiaries.length === 0 ? (
              <View style={styles.emptyPanel}>
                <MaterialIcons name="low-priority" size={24} color="#c0c8d4" />
                <Text style={styles.emptyPanelText}>No Tertiary for this subcategory selection.</Text>
              </View>
            ) : (
              visibleTertiaries.map((tertiary) => {
                const isTerEditable =
                  editableTertiaryIds === undefined || editableTertiaryIds.has(tertiary.id);
                return (
                  <React.Fragment key={tertiary.id}>
                    <ListRow
                      label={tertiary.name}
                      fieldsCount={
                        tertiary.id === activeTertiaryId
                          ? tertiaryFieldsQuery.fields.length
                          : tertiary.fields?.length ?? 0
                      }
                      selected={tertiary.id === activeTertiaryId}
                      itemActive={tertiary.active !== false}
                      onPress={() => handleSelectTertiary(tertiary)}
                      onAddFields={isTerEditable ? () =>
                        openFieldsEditorForTertiary(
                          activeCategoryId || "",
                          activeSubcategoryId || "",
                          tertiary.id
                        )
                        : undefined}
                      onEdit={isTerEditable ? () =>
                        openEditTertiaryEditor(activeCategoryId || "", activeSubcategoryId || "", tertiary.id)
                        : undefined}
                      onToggle={isTerEditable ? (val) => handleToggleTertiary(tertiary.id, val) : undefined}
                    />
                  </React.Fragment>
                );
              })
            )}
          </ScrollView>
        </View>
      </View>

      {/* MODALS */}
      <AddCategoryWizard
        visible={modals.addCategory}
        onClose={closeAddCategoryModal}
        onSave={handleSaveCategoryWizard}
        existingNames={localCategories.map((category) => category.name)}
        teams={teams}
      />

      <AddSubcategoryModal
        visible={modals.addSubcategory}
        onClose={closeAddSubcategoryModal}
        onSave={handleAddSubcategory}
        existingNames={visibleSubcategories.map((subcategory) => subcategory.name)}
        category={
          localCategories.find((c) => c.id === selectedContext.category)?.name || "—"
        }
        onOpenFieldsModal={openAdditionalFieldsModal}
      />

      <AddTertiaryModal
        visible={modals.addTertiary}
        onClose={closeAddTertiaryModal}
        onSave={handleAddTertiary}
        existingNames={visibleTertiaries.map((tertiary) => tertiary.name)}
        category={
          localCategories.find((c) => c.id === selectedContext.category)?.name || "—"
        }
        subcategory={
          localSubcategories.find((s) => s.id === selectedContext.subcategory)?.name || "—"
        }
        onOpenFieldsModal={openAdditionalFieldsModal}
      />

      <AdditionalFieldsModal
        visible={modals.additionalFields}
        onClose={closeAdditionalFieldsModal}
        key={`${selectedContext.modalType || "none"}-${selectedContext.category}-${selectedContext.subcategory}-${selectedContext.tertiary}`}
        onSave={handleSaveFields}
        onDeleteField={handleDeleteField}
        loading={modalFieldsLoading}
        saveError={fieldsSaveError}
        subtitle={
          selectedContext.modalType === "tertiary"
            ? "Tertiary Category"
            : selectedContext.modalType === "subcategory"
            ? "Subcategory"
            : "Category"
        }
        initialFields={modalInitialFields}
      />

      <EditCategoryModal
        visible={modals.editCategory}
        onClose={closeEditCategoryModal}
        onSave={handleEditCategory}
        categoryName={
          localCategories.find((c) => c.id === selectedContext.category)?.name || ""
        }
      />

      <EditSubcategoryModal
        visible={modals.editSubcategory}
        onClose={closeEditSubcategoryModal}
        onSave={handleEditSubcategory}
        category={
          localCategories.find((c) => c.id === selectedContext.category)?.name || "—"
        }
        subcategoryName={
          localSubcategories.find((s) => s.id === selectedContext.subcategory)?.name || ""
        }
        onOpenFieldsModal={openAdditionalFieldsModal}
      />

      <EditTertiaryModal
        visible={modals.editTertiary}
        onClose={closeEditTertiaryModal}
        onSave={handleEditTertiary}
        category={
          localCategories.find((c) => c.id === selectedContext.category)?.name || "—"
        }
        subcategory={
          localSubcategories.find((s) => s.id === selectedContext.subcategory)?.name || "—"
        }
        tertiaryName={
          localTertiaries.find((t) => t.id === selectedContext.tertiary)?.name || ""
        }
        onOpenFieldsModal={openAdditionalFieldsModal}
      />

    </View>
  );
};

export default CategoryMapping;
